import {
  type MeetingAppGateAnswer,
  type MeetingAppsUpdatedPayload,
} from '@electron/shared/meetings.types';
import { type z } from 'zod';

import { electronApis } from '@/features/electron-api';
import { logError } from '@/lib/errorLogging';

import { normalizeMeetingAppBundleKey } from './meeting-app-allowlist';

type MeetingAppIdentity = {
  bundleIdentifier?: string;
  name?: string;
  gateAnswer?: MeetingAppGateAnswer;
};

type MeetingAppOffering = 'may_auto_start' | 'card_only' | 'not_offered';

type MeetingAppOffer = {
  appKey: string;
  notificationId: string;
  autoStartAttemptId: string;
  offering: Exclude<MeetingAppOffering, 'not_offered'>;
  isSent: boolean;
};

type LiveAutoStart = { attemptId: string; appKey: string };

const getMeetingAppKey = ({ bundleIdentifier, name }: MeetingAppIdentity) =>
  normalizeMeetingAppBundleKey(bundleIdentifier ?? name ?? '');

const offeringByGateAnswer: Record<MeetingAppGateAnswer, MeetingAppOffering> = {
  meeting: 'may_auto_start',
  cant_tell: 'card_only',
  pending: 'not_offered',
  not_meeting: 'not_offered',
};

const offeringOf = ({ gateAnswer }: MeetingAppIdentity) =>
  offeringByGateAnswer[gateAnswer ?? 'meeting'];

export const isAnsweredMeeting = (app: MeetingAppIdentity) =>
  offeringOf(app) === 'may_auto_start';

const keepsOffer = (offer: MeetingAppOffer, offering: MeetingAppOffering) =>
  offering === offer.offering ||
  (offer.offering === 'card_only' && offering === 'may_auto_start');

class MeetingAppDetections<App extends MeetingAppIdentity> {
  private detectedApps: App[] = [];
  private offeredAppKeys = new Set<string>();
  private liveAutoStartAppKeys = new Map<string, string>();
  private currentOffer: MeetingAppOffer | undefined;

  update(apps: App[]): MeetingAppOffer | undefined {
    this.detectedApps = apps;
    const offeringByAppKey = new Map(
      apps.map((app) => [getMeetingAppKey(app), offeringOf(app)])
    );
    const offeringOfKey = (appKey: string) =>
      offeringByAppKey.get(appKey) ?? 'not_offered';
    this.offeredAppKeys = new Set(
      [...this.offeredAppKeys].filter((appKey) => offeringByAppKey.has(appKey))
    );
    this.cancelAutoStartsWhere(
      ({ appKey }) => offeringOfKey(appKey) !== 'may_auto_start'
    );
    const withdrawnOffer = this.takeCurrentOfferIf(
      (offer) => !keepsOffer(offer, offeringOfKey(offer.appKey))
    );
    if (withdrawnOffer) this.offeredAppKeys.delete(withdrawnOffer.appKey);
    return withdrawnOffer;
  }

  get detectedAppCount() {
    return this.detectedApps.length;
  }

  get currentApps(): readonly App[] {
    return this.detectedApps;
  }

  nextAppToOffer({ showsCards }: { showsCards: boolean }): App | undefined {
    if (this.currentOffer) return undefined;
    return this.detectedApps.find((app) => {
      const offering = offeringOf(app);
      const isOffered =
        offering === 'may_auto_start' ||
        (offering === 'card_only' && showsCards);
      return isOffered && !this.offeredAppKeys.has(getMeetingAppKey(app));
    });
  }

  beginOffer(
    app: App,
    offer: { notificationId: string; autoStartAttemptId: string }
  ) {
    const appKey = getMeetingAppKey(app);
    const offering = offeringOf(app);
    if (offering === 'not_offered') return;
    this.offeredAppKeys.add(appKey);
    this.currentOffer = { appKey, offering, isSent: false, ...offer };
  }

  markOfferSent(autoStartAttemptId: string) {
    if (this.currentOffer?.autoStartAttemptId !== autoStartAttemptId) return;
    this.currentOffer.isSent = true;
  }

  cardAnsweredMeeting(): { app: App; card: MeetingAppOffer } | undefined {
    const card = this.currentOffer;
    if (card?.offering !== 'card_only') return undefined;
    const app = this.detectedApps.find(
      (detectedApp) =>
        getMeetingAppKey(detectedApp) === card.appKey &&
        offeringOf(detectedApp) === 'may_auto_start'
    );
    return app && { app, card };
  }

  withdrawCardToOfferAgain(autoStartAttemptId: string) {
    const isSameCard =
      this.cardAnsweredMeeting()?.card.autoStartAttemptId ===
      autoStartAttemptId;
    const card = isSameCard ? this.takeCurrentOfferIf(() => true) : undefined;
    if (card) this.offeredAppKeys.delete(card.appKey);
    return card;
  }

  allowAutoStart(autoStartAttemptId: string) {
    const offer = this.currentOffer;
    if (offer?.autoStartAttemptId !== autoStartAttemptId) return;
    this.liveAutoStartAppKeys.set(autoStartAttemptId, offer.appKey);
  }

  isCurrentOffer(autoStartAttemptId: string) {
    return this.currentOffer?.autoStartAttemptId === autoStartAttemptId;
  }

  endOffer(autoStartAttemptId: string) {
    this.takeCurrentOfferIf(
      (offer) => offer.autoStartAttemptId === autoStartAttemptId
    );
  }

  cancelUnsentOffer(autoStartAttemptId: string) {
    const offer = this.takeCurrentOfferIf(
      (currentOffer) => currentOffer.autoStartAttemptId === autoStartAttemptId
    );
    if (!offer) return;
    this.offeredAppKeys.delete(offer.appKey);
    this.liveAutoStartAppKeys.delete(offer.autoStartAttemptId);
  }

  isAutoStartLive(autoStartAttemptId: string | undefined) {
    return (
      !!autoStartAttemptId && this.liveAutoStartAppKeys.has(autoStartAttemptId)
    );
  }

  cancelOtherAutoStarts(autoStartAttemptId: string | undefined) {
    this.cancelAutoStartsWhere(
      ({ attemptId }) => attemptId !== autoStartAttemptId
    );
    return this.takeCurrentOfferIf(
      (offer) => offer.autoStartAttemptId !== autoStartAttemptId
    );
  }

  stopOfferingDetectedApps() {
    const stoppedAppKeys = new Set(this.detectedApps.map(getMeetingAppKey));
    for (const appKey of stoppedAppKeys) this.offeredAppKeys.add(appKey);
    this.cancelAutoStartsWhere(({ appKey }) => stoppedAppKeys.has(appKey));
    return this.takeCurrentOfferIf((offer) => stoppedAppKeys.has(offer.appKey));
  }

  private cancelAutoStartsWhere(
    shouldCancel: (autoStart: LiveAutoStart) => boolean
  ) {
    for (const [attemptId, appKey] of this.liveAutoStartAppKeys) {
      if (shouldCancel({ attemptId, appKey })) {
        this.liveAutoStartAppKeys.delete(attemptId);
      }
    }
  }

  private takeCurrentOfferIf(shouldTake: (offer: MeetingAppOffer) => boolean) {
    const offer = this.currentOffer;
    if (!offer || !shouldTake(offer)) return undefined;
    this.currentOffer = undefined;
    return offer;
  }
}

export type DetectedMeetingApp = z.infer<
  typeof MeetingAppsUpdatedPayload
>['apps'][number];

export const meetingAppDetections =
  new MeetingAppDetections<DetectedMeetingApp>();

export function withdrawMeetingAppOffer(offer: MeetingAppOffer | undefined) {
  if (!offer) return;
  electronApis.lbNotifications
    .dismissById(offer.notificationId)
    .catch((error) => {
      logError(
        error,
        '[meeting-app-detection] Failed to withdraw meeting-detected notification'
      );
    });
}
